import { computed } from 'vue'
import { logger } from '@/utils/logger'
import type { IHSLColor } from '@/types/theme-types'
import type { IStyleConfiguration } from '@/types/style-types'
import { validateZoomFactor } from '@/types/style-types'
import {
  THEME_CONSTANTS,
  STYLE_CONSTANTS,
  WIDGET_CONSTANTS,
} from '@/types/constants'
import { useChatbotContext } from '@/composables/useChatbotContext'
import { getOrCreateChatbotStore } from '@/stores/chatbot'

export function useTheme() {
  const context = useChatbotContext()

  const getChatbotStore = () => {
    const storeKey = context?.storeKey.value
    if (!storeKey) {
      return null
    }
    const storeFactory = getOrCreateChatbotStore(storeKey)
    return storeFactory()
  }

  function hexToHsl(hex: string | null | undefined): IHSLColor {
    if (!hex) {
      return { h: 0, s: 0, l: 50 }
    }
    let cleanHex = hex.replace('#', '')
    if (cleanHex.length === 3) {
      cleanHex = cleanHex
        .split('')
        .map(char => char + char)
        .join('')
    }

    const r = parseInt(cleanHex.substring(0, 2), 16) / 255
    const g = parseInt(cleanHex.substring(2, 4), 16) / 255
    const b = parseInt(cleanHex.substring(4, 6), 16) / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const diff = max - min

    let h = 0
    let s = 0
    const l = (max + min) / 2

    if (diff !== 0) {
      s = l > 0.5 ? diff / (2 - max - min) : diff / (max + min)

      switch (max) {
        case r:
          h = ((g - b) / diff + (g < b ? 6 : 0)) / 6
          break
        case g:
          h = ((b - r) / diff + 2) / 6
          break
        case b:
          h = ((r - g) / diff + 4) / 6
          break
      }
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100),
    }
  }

  function getRelativeLuminance(hsl: IHSLColor): number {
    const h = hsl.h / 360
    const s = hsl.s / 100
    const l = hsl.l / 100

    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }

    let r, g, b
    if (s === 0) {
      r = g = b = l
    } else {
      const q = l < 0.5 ? l * (1 + s) : l + s - l * s
      const p = 2 * l - q
      r = hue2rgb(p, q, h + 1 / 3)
      g = hue2rgb(p, q, h)
      b = hue2rgb(p, q, h - 1 / 3)
    }

    const gamma = (c: number) =>
      c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
    return 0.2126 * gamma(r) + 0.7152 * gamma(g) + 0.0722 * gamma(b)
  }

  function getContrastColor(
    backgroundColor: string
  ):
    | typeof THEME_CONSTANTS.CONTRAST_COLORS.LIGHT
    | typeof THEME_CONSTANTS.CONTRAST_COLORS.DARK {
    const hsl = hexToHsl(backgroundColor)
    const luminance = getRelativeLuminance(hsl)
    return luminance < 0.5
      ? THEME_CONSTANTS.CONTRAST_COLORS.LIGHT
      : THEME_CONSTANTS.CONTRAST_COLORS.DARK
  }

  const initializeTheme = (): void => {
    const store = getChatbotStore()
    const brandColor = store?.brand?.brandColor
    if (!brandColor) {
      logger.warn('No brand color available, using default theme', undefined, {
        component: 'useTheme',
      })
      return
    }
    applyTheme()
  }

  const applyTheme = (): void => {
    const store = getChatbotStore()
    const brandColor = store?.brand?.brandColor
    if (!brandColor) {
      logger.warn('No brand color available, using default theme', undefined, {
        component: 'useTheme',
      })
      return
    }

    const chatbotElement = document.querySelector(
      `.${WIDGET_CONSTANTS.CSS_CLASSES.CHATBOT}`
    )
    if (!chatbotElement) {
      logger.warn('Chatbot element not found, cannot apply theme', undefined, {
        component: 'useTheme',
      })
      return
    }

    const hsl = hexToHsl(brandColor)
    const hslString = `${hsl.h} ${hsl.s}% ${hsl.l}%`

    ;(chatbotElement as HTMLElement).style.setProperty(
      THEME_CONSTANTS.CSS_VARIABLES.PRIMARY,
      hslString
    )

    const contrastColor = getContrastColor(brandColor)
    ;(chatbotElement as HTMLElement).style.setProperty(
      THEME_CONSTANTS.CSS_VARIABLES.PRIMARY_CONTRAST,
      contrastColor
    )

    logger.info(
      'Theme applied',
      { brandColor, contrastColor },
      { component: 'useTheme' }
    )
  }

  const resetTheme = (): void => {
    const chatbotElement = document.querySelector('.siddchatbot')
    if (!chatbotElement) {
      return
    }

    Object.values(THEME_CONSTANTS.CSS_VARIABLES).forEach(cssVar => {
      ;(chatbotElement as HTMLElement).style.removeProperty(cssVar)
    })
    ;(chatbotElement as HTMLElement).style.removeProperty(
      STYLE_CONSTANTS.CSS_VARIABLES.ZOOM_FACTOR
    )
  }

  const applyZoomFactor = (zoom: number): void => {
    try {
      const validatedZoom = validateZoomFactor(zoom)
      const chatbotElement = document.querySelector('.siddchatbot')

      if (!chatbotElement) {
        logger.warn('Chatbot element not found for zoom factor application', {
          component: 'useTheme',
        })
        return
      }

      ;(chatbotElement as HTMLElement).style.setProperty(
        STYLE_CONSTANTS.CSS_VARIABLES.ZOOM_FACTOR,
        validatedZoom.toString()
      )

      logger.debug(
        'Zoom factor applied',
        {
          zoom: validatedZoom,
          original: zoom,
        },
        { component: 'useTheme' }
      )
    } catch (error) {
      logger.error(
        'Failed to apply zoom factor',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'useTheme' }
      )
    }
  }

  const initializeStyles = (config?: IStyleConfiguration): void => {
    if (!config) {
      logger.debug('No style configuration provided', {
        component: 'useTheme',
      })
      return
    }

    logger.debug('Initializing styles', { config }, { component: 'useTheme' })

    if (config.zoomFactor !== undefined) {
      applyZoomFactor(config.zoomFactor)
    }
  }

  const currentBrandColor = computed(() => {
    const store = getChatbotStore()
    return store?.brand?.brandColor
  })

  return {
    // State
    currentBrandColor,

    // Utility functions
    hexToHsl,
    getContrastColor,

    // Theme management
    initializeTheme,
    applyTheme,
    resetTheme,

    applyZoomFactor,
    initializeStyles,
  }
}

export default useTheme
